import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RoleSelect, { RoleOption as RSOption } from "./RoleSelect";
import CoachSportsField from "./CoachSportsField";
import TeamMemberConfigurations from "./TeamMemberConfigurations";
import UploadImageModal from "./UploadImageModal";
import ProfileAvatar from "./ProfileAvatar";
import PasswordForm from "./PasswordForm";
import DeleteTeamMemberModal from "./DeleteTeamMemberModal";
import { buildApiUrl, API_BASE_URL } from "../utils/apiConfig";
import { getCurrentUserName, uploadUserAvatar, uploadCompanyLogo, clearCompanyLogoCache, fetchCompanyLogoBlobUrl, clearMyAvatarCache, clearUserAvatarCache, toAbsoluteUrl, resolveCompanyLogoDisplayUrl } from "../utils/profilePictureUtils";
import {isStaff, getTermsAccepted, setHasProfile, isAdmin, isCoach} from "../utils/auth";
import { formatRoleLabel } from "../utils/roleLabels";
import { useAuth } from "../contexts/AuthContext";
import { useIncompleteAdminProfileOptional } from "../contexts/IncompleteAdminProfileContext";
import {
    hasValidCompanyOrSchool,
    isAdminStaffProfileSetupComplete,
    isPlaceholderCompanyName,
} from "../utils/adminProfileSetup";
import { parseProfileRoleId } from "../utils/adminProfilePayload";
import { parseApiErrorMessage } from "../utils/apiErrorMessage";
import "../styles/forms.css";
import "../styles/profile-form.css";

export type Profile = {
    firstName: string;
    lastName: string;
    email: string;
    title: string;
    role: string;
    roleId?: number;
    telephone: string;
    companyName?: string;
    profileImageUrl?: string;
    companyLogoUrl?: string;
    cleverDistrictId?: string;
    cleverSchoolId?: string;
    cleverSchoolName?: string;
    cleverDistrictName?: string;
    cleverSisId?: string;
    sportId?: number;
    sportIds?: number[];
    internalCoachPreReadEnabled?: boolean;
    manageStudentAthletesEnabled?: boolean;
};

export type ProfileFormProps = {
    loadUrl: string;
    saveUrl: string;
    useCookies?: boolean;
    roles?: string[];
    rolesOptionsUrl?: string;
    onSaved?: (saved: Profile) => void;
    onClose?: () => void;
    afterSaveTo?: string | ((saved: Profile) => string);
    onPasswordChanged?: () => void | Promise<void>;
    requirePasswordChange?: boolean;
    userId?: string | number; // Optional userId for admin editing team members
    showDeleteAccount?: boolean; // Show delete account link at bottom of password form
    onDeleteAccount?: () => void; // Handler for delete account
    isTeamMemberEdit?: boolean; // Flag to indicate editing a team member (hides avatar upload, shows delete)
    firstTimeSetup?: boolean; // Flag to indicate first-time profile creation after login
};

type RoleOption = { id: number; name: string };

const DEFAULT_OPTIONS_URL = buildApiUrl("api/company/members/add/view");

/** Helper to convert absolute URLs back to relative paths for backend storage */
function toRelative(u?: string | null): string | undefined {
    if (!u) return undefined;
    const raw = u.trim();
    if (!raw) return undefined;
    // If it's a data URL or blob URL, keep it as is
    if (/^(data:|blob:)/i.test(raw)) return raw;
    // If it starts with the API_BASE_URL, strip it
    if (raw.startsWith(API_BASE_URL)) {
        const relativePath = raw.substring(API_BASE_URL.length);
        // If it's just a filename (starts with / and has no other slashes), strip the leading /
        if (relativePath.startsWith('/') && !relativePath.substring(1).includes('/')) {
            return relativePath.substring(1);
        }
        return relativePath;
    }
    // If it's an absolute URL with a different host, keep it as is
    if (/^https?:\/\//i.test(raw)) return raw;
    // If it starts with //, keep it as is
    if (raw.startsWith("//")) return raw;
    // If it starts with a single / and is just a filename (no other slashes), strip it
    if (raw.startsWith('/') && !raw.substring(1).includes('/')) {
        return raw.substring(1);
    }
    // Already relative
    return raw;
}

// normalize roles so variants match
function canon(name?: string) {
    const k = (name || "").toLowerCase().replace(/[^a-z]/g, "");
    if (!k) return "";
    if (k.includes("administrator") || k.includes("admin")) return "administrator";
    if (k.includes("staff")) return "staff";
    if (k.includes("editor")) return "editor";
    if (k.includes("viewer") || k.includes("read")) return "viewer";
    if (k.includes("athlete") || k.includes("player")) return "athlete";
    return k;
}

/* ------------ component ------------ */

export default function ProfileForm({
                                        loadUrl,
                                        saveUrl,
                                        useCookies = false,
                                        roles,
                                        rolesOptionsUrl = DEFAULT_OPTIONS_URL,
                                        onSaved,
                                        onClose,
                                        afterSaveTo,
                                        onPasswordChanged,
                                        requirePasswordChange = false,
                                        userId,
                                        showDeleteAccount = false,
                                        onDeleteAccount,
                                        isTeamMemberEdit = false,
                                        firstTimeSetup = false,
                                    }: ProfileFormProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { userInfo, authProvider, hasProfile: authHasProfile } = useAuth();
    const incompleteProfileCtx = useIncompleteAdminProfileOptional();
    const setSetupIncomplete = incompleteProfileCtx?.setSetupIncomplete;
    const registerValidationHandler = incompleteProfileCtx?.registerValidationHandler;



    const [data, setData] = useState<Profile>({
        firstName: "",
        lastName: "",
        email: "",
        title: "",
        role: "",
        roleId: undefined,
        telephone: "",
        companyName: "",
        profileImageUrl: "",
        companyLogoUrl: "",
        cleverDistrictId: undefined,
        cleverSchoolId: undefined,
        cleverSchoolName: undefined,
        cleverDistrictName: undefined,
        cleverSisId: undefined,
        sportId: undefined,
        sportIds: [],
        internalCoachPreReadEnabled: false,
        manageStudentAthletesEnabled: false,
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [navigating, setNavigating] = useState(false);
    const [error, setError] = useState<string>("");
    const [ok, setOk] = useState<string>("");
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [roleOptions, setRoleOptions] = useState<RoleOption[] | null>(null);
    const [loadingRoles, setLoadingRoles] = useState(false);

    const [showUploadAvatar, setShowUploadAvatar] = useState(false);
    const [showUploadLogo, setShowUploadLogo] = useState(false);
    const [pendingAvatar, setPendingAvatar] = useState<File | null>(null);
    const [pendingLogo, setPendingLogo] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [logoImageError, setLogoImageError] = useState(false);
    const [deletingStaff, setDeletingStaff] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Add state for company logo blob URL (like OuterLayout)
    const [companyLogoBlobUrl, setCompanyLogoBlobUrl] = useState<string | null>(null);

    // Track if terms have been accepted to trigger data reload
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [termsAccepted, setTermsAccepted] = useState(getTermsAccepted());

    /** Set only on profile load and after save – never from keystrokes – so the school name lock doesn't flip during typing */
    const [serverCleverSchoolName, setServerCleverSchoolName] = useState<string | undefined>(undefined);

    const isAdminEditor = isAdmin();
    const isCurrentUserStaff = isStaff() || isCoach();
    const [isNewProfile, setIsNewProfile] = useState(false);
    /** Setup completeness from the server-loaded profile — not live form edits (used for cancel / nav gating). */
    const [serverProfileSetupComplete, setServerProfileSetupComplete] = useState<boolean | null>(null);

    // Track if role has been initialized to prevent infinite loops
    const roleInitializedRef = useRef(false);

    // Track last avatar upload timestamp for cache busting
    const [avatarUploadTimestamp, setAvatarUploadTimestamp] = useState<number>(0);

    // Store the initial email to determine if editing self (don't recalculate on every data change)
    const initialEmailRef = useRef<string | null>(null);

    // Store userInfo.userId in a ref to prevent unnecessary re-renders when AuthContext updates
    const userIdRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        userIdRef.current = userInfo?.userId;
    }, [userInfo?.userId]);

    // Determine if we're editing the logged-in user's own profile (re-read each render; do not
    // cache on initialEmailRef — that left isEditingSelf false after re-login until unrelated re-renders).
    const isEditingSelf = (() => {
        const loggedInUserEditValue = localStorage.getItem("loggedInUserEdit");

        if (loggedInUserEditValue === "false") return false;
        if (loggedInUserEditValue === "true") return true;

        const currentUserId = getCurrentUserName();
        if (initialEmailRef.current === null) {
            return false;
        }

        return !initialEmailRef.current || initialEmailRef.current === currentUserId;
    })();

    const uploadMultipart = useCallback(async (url: string, file: File) => {
        const fd = new FormData();
        fd.append("file", file);

        const headers: HeadersInit = {};

        const res = await fetch(url, {
            method: "POST",
            headers,
            body: fd,
            credentials: "include", // Send HTTP-only cookies
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        return json?.url || json?.profileImageUrl || json?.companyLogoUrl || json?.location;
    }, [useCookies]);

    /** roles options — add-member catalog is admin/staff only; coaches get roleId from the profile GET. */
    const fetchRoleOptions = useCallback(async () => {
        const termsAccepted = getTermsAccepted();

        if (!termsAccepted) {
            return;
        }
        if (!isAdminEditor) {
            setRoleOptions([]);
            setLoadingRoles(false);
            return;
        }

        setLoadingRoles(true);
        try {
            const res = await fetch(rolesOptionsUrl!, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                credentials: "include",
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            const opts: RoleOption[] = Array.isArray(json?.profileTypes) ? json.profileTypes : [];
            setRoleOptions(opts.length ? opts : []);
        } catch {
            setRoleOptions([]);
        } finally {
            setLoadingRoles(false);
        }
    }, [rolesOptionsUrl, isAdminEditor]);

    // NEW: object URL previews for selected files
    useEffect(() => {
        if (!pendingAvatar) { setAvatarPreview(null); return; }
        const url = URL.createObjectURL(pendingAvatar);
        setAvatarPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [pendingAvatar]);

    useEffect(() => {
        if (!pendingLogo) { setLogoPreview(null); return; }
        const url = URL.createObjectURL(pendingLogo);
        setLogoPreview(url);
        // Reset logo error when new logo is selected
        setLogoImageError(false);
        return () => URL.revokeObjectURL(url);
    }, [pendingLogo]);

    useEffect(() => { fetchRoleOptions(); }, [fetchRoleOptions, termsAccepted]);

    // Handler for logo upload button click - validate company name first
    const handleLogoUploadClick = () => {
        if (!data.companyName?.trim() && !data.cleverSchoolName?.trim()) {
            setFieldErrors(prev => ({
                ...prev,
                companyName: "Please enter a company name before uploading a logo. Note: Company name cannot be changed after a logo is uploaded."
            }));
            return;
        }
        // Clear any previous error
        setFieldErrors(prev => {
            const { companyName, ...rest } = prev;
            return rest;
        });
        setShowUploadLogo(true);
    };

    // Listen for termsAccepted event to reload data
    useEffect(() => {
        const handleTermsAccepted = () => {
            setTermsAccepted(true);
        };

        window.addEventListener('termsAccepted', handleTermsAccepted);
        return () => window.removeEventListener('termsAccepted', handleTermsAccepted);
    }, []);

    // Fetch company logo via authenticated API (reliable for SVG/private S3); bust cache when URL changes.
    const companyLogoFetchKeyRef = useRef<string>("");

    const refreshCompanyLogoPreview = useCallback(
        async (email: string, bustCache: boolean) => {
            if (!email || !isAdminEditor || canon(data.role) !== "administrator") {
                setCompanyLogoBlobUrl(null);
                return;
            }
            try {
                const blobUrl = await fetchCompanyLogoBlobUrl(email, bustCache);
                setCompanyLogoBlobUrl(blobUrl);
                setLogoImageError(false);
            } catch {
                setCompanyLogoBlobUrl(null);
            }
        },
        [data.role, isAdminEditor],
    );

    useEffect(() => {
        if (!data.email || !isAdminEditor || canon(data.role) !== "administrator") {
            setCompanyLogoBlobUrl(null);
            return;
        }

        const fetchKey = `${data.email}|${data.companyLogoUrl ?? ""}`;
        const bustCache = companyLogoFetchKeyRef.current !== fetchKey;
        companyLogoFetchKeyRef.current = fetchKey;

        void refreshCompanyLogoPreview(data.email, bustCache);
    }, [data.email, data.companyLogoUrl, data.role, isAdminEditor, refreshCompanyLogoPreview]);

    // Pre-populate validation error for Clever admins/staff with missing school/company name
    useEffect(() => {
        const isClever = authProvider === "clever";
        const requiresCompanyName =
            (isEditingSelf ||
                (location.pathname === "/add-admin-profile" &&
                    (firstTimeSetup || !authHasProfile || isNewProfile))) &&
            (isAdminEditor || isCurrentUserStaff);

        // Avoid showing validation while we're still loading or navigating away
        if (loading || loadingRoles || navigating) return;

        const companyName = (data.companyName ?? "").trim();
        const needsSchool =
            isClever &&
            requiresCompanyName &&
            !data.cleverSchoolName &&
            !companyName;

        setFieldErrors((prev) => {
            if (!needsSchool) {
                // Only clear the Clever pre-populated message — never strip save-time validation errors.
                if (prev.companyName === "School name is required for Clever accounts.") {
                    const { companyName: _ignored, ...rest } = prev;
                    return rest;
                }
                return prev;
            }

            // Avoid unnecessary re-renders if the error is already set
            if (prev.companyName === "School name is required for Clever accounts.") {
                return prev;
            }

            return {
                ...prev,
                companyName: "School name is required for Clever accounts.",
            };
        });
    }, [
        authProvider,
        isEditingSelf,
        isAdminEditor,
        isCurrentUserStaff,
        location.pathname,
        firstTimeSetup,
        authHasProfile,
        isNewProfile,
        data.companyName,
        data.cleverSchoolName,
        loading,
        loadingRoles,
        navigating,
    ]);

    /** load profile */
    useEffect(() => {
        // Don't load profile if terms haven't been accepted yet
        const termsAccepted = getTermsAccepted();

        if (!termsAccepted) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setServerProfileSetupComplete(null);
            setError("");
            try {
                const res = await fetch(loadUrl, {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    credentials: "include", // Send HTTP-only cookies
                });

                if (res.status === 404) {
                    if (!cancelled) {
                        setIsNewProfile(true);
                        setServerProfileSetupComplete(false);
                        setServerCleverSchoolName(undefined); // No profile yet = no server school name
                        // For new profiles, populate email with logged-in user's email from AuthContext
                        const loggedInUserEmail = userIdRef.current;
                        setData((d) => ({
                            ...d,
                            email: loggedInUserEmail || d.email,
                            role: d.role || "",
                            roleId: d.roleId ?? undefined
                        }));
                        initialEmailRef.current = loggedInUserEmail || null;
                        setLoading(false);
                    }
                    return;
                }

                const json = res.status === 404 ? null : (res.ok ? await res.json() : null);

                if (json === null && res.status !== 404) throw new Error(`HTTP ${res.status}`);

                if (!cancelled && json) {
                    const body = json as Partial<Profile>;

                    // Handle both camelCase and snake_case field names for company logo
                    const rawCompanyLogo = body.companyLogoUrl
                        || (json as any).company_logo_url
                        || (json as any).companyLogo
                        || (json as any).company_logo;

                    const companyLogoUrl = toAbsoluteUrl(rawCompanyLogo);

                    // Extract role from multiple possible field names (role, profileType, roleName)
                    const roleFromBody = (json as any).role ?? (json as any).profileType ?? (json as any).roleName;
                    const roleIdFromBody =
                        parseProfileRoleId((json as any).roleId) ??
                        parseProfileRoleId((json as any).profileTypeId);

                    const loadedEmail = (
                        body.email ?? (json as any).email ?? userIdRef.current ?? ""
                    ).trim();
                    let loadedCompanyName = (body.companyName ?? "").trim();
                    if (isPlaceholderCompanyName(loadedCompanyName, loadedEmail)) {
                        loadedCompanyName = "";
                    }
                    const loadedCleverSchoolName =
                        (body.cleverSchoolName ?? (json as any).clever_school_name ?? "").trim() || undefined;

                    setServerProfileSetupComplete(
                        isAdminStaffProfileSetupComplete({
                            title: body.title,
                            telephone: body.telephone,
                            companyName: loadedCompanyName,
                            cleverSchoolName: loadedCleverSchoolName,
                            email: loadedEmail,
                        }),
                    );

                    setData((d) => {
                        const rawRole =
                            typeof roleFromBody === "string"
                                ? roleFromBody
                                : (d.role ?? "");
                        const normalizedRole =
                            typeof rawRole === "string" ? rawRole.trim() : "";

                        return {
                            ...d,
                            ...body,
                            companyName: loadedCompanyName,
                            cleverSchoolName: loadedCleverSchoolName ?? d.cleverSchoolName,
                            role: normalizedRole,
                            roleId: roleIdFromBody ?? d.roleId,
                            profileImageUrl: toAbsoluteUrl(body.profileImageUrl) ?? d.profileImageUrl,
                            companyLogoUrl: companyLogoUrl ?? d.companyLogoUrl,
                            sportId: typeof (json as any).sportId === "number" ? (json as any).sportId : d.sportId,
                            sportIds: Array.isArray((json as any).sports)
                                ? (json as any).sports
                                    .map((sport: { id?: number }) => Number(sport?.id))
                                    .filter((id: number) => Number.isFinite(id) && id > 0)
                                : Array.isArray((json as any).sportIds)
                                    ? (json as any).sportIds
                                    : (typeof (json as any).sportId === "number" ? [(json as any).sportId] : d.sportIds),
                            internalCoachPreReadEnabled: Boolean((json as any).internalCoachPreReadEnabled),
                            manageStudentAthletesEnabled: Boolean((json as any).manageStudentAthletesEnabled),
                        };
                    });
                    const cleverSchoolFromLoad = loadedCleverSchoolName;
                    setServerCleverSchoolName(typeof cleverSchoolFromLoad === "string" ? cleverSchoolFromLoad : undefined);
                    // Set the initial email reference after loading profile
                    initialEmailRef.current = (json as any).email ?? null;
                }
            } catch (e: any) {
                if (!cancelled) setError(e?.message || "Failed to load profile.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [loadUrl, useCookies, isAdminEditor, termsAccepted]);

    /** Set role to Administrator for first-time setup after subscription */
    /** Set role for new profiles based on user's actual assigned role */
    useEffect(() => {
        if (isNewProfile && roleOptions && roleOptions.length > 0 && !data.role) {
            // Get the user's assigned role from AuthContext or localStorage
            const userRole = userInfo?.role || localStorage.getItem('userRole') || '';

            // Find the matching role option
            const matchingRole = roleOptions.find((o) => canon(o.name) === canon(userRole));
            if (matchingRole) {
                setData((d) => ({
                    ...d,
                    role: matchingRole.name,
                    roleId: matchingRole.id
                }));
            }
        }
    }, [isNewProfile, roleOptions, data.role, userInfo?.role]);

    /** roles prop fallback */
    useEffect(() => {
        if (roleOptions === null && roles && roles.length > 0) {
            setRoleOptions(roles.map<RoleOption>((name, i) => ({ id: i + 1, name })));
        }
    }, [roles, roleOptions]);

    /** keep selection coherent when options/data change */
    useEffect(() => {
        if (!roleOptions || roleOptions.length === 0) return;

        // Don't run if we haven't loaded profile data yet (role would be empty)
        if (!data.role && !data.roleId) return;

        // Only run this logic once when roleOptions first loads AND we have profile data
        if (roleInitializedRef.current) return;


        setData((d) => {
            // First try to match by ID if we have one
            const byId = d.roleId != null ? roleOptions.find((o) => o.id === d.roleId) : undefined;
            // Then try to match by name using canonical comparison
            const byName = d.role ? roleOptions.find((o) => canon(o.name) === canon(d.role)) : undefined;

            // Use the matched option, but DON'T default to roleOptions[0] if we have role data
            const chosen = byName ?? byId;


            if (chosen && (d.role !== chosen.name || d.roleId !== chosen.id)) {
                roleInitializedRef.current = true;
                return { ...d, role: chosen.name, roleId: chosen.id };
            }
            // Only mark as initialized if we actually found a match
            // This allows the effect to run again if profile data loads after role options
            if (chosen) {
                roleInitializedRef.current = true;
            }
            return d;
        });
    }, [roleOptions, data.role, data.roleId]); // Add data.role and data.roleId as dependencies

    const set = (k: keyof Profile) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setData((s) => ({ ...s, [k]: e.target.value }));
            // Clear field error when user starts typing
            if (fieldErrors[k]) {
                setFieldErrors(prev => ({ ...prev, [k]: "" }));
            }
        };

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email || "");

    // Fixed phone validation - normalize the input first, then test against a simpler pattern
    const normalizedPhone = (data.telephone || "").replace(/\D/g, ""); // Remove all non-digits
    const phoneOk = normalizedPhone.length === 0 || // Allow empty (required check handles this separately)
                   /^1?[2-9][0-9]{2}[0-9]{7}$/.test(normalizedPhone); // US phone: optional 1 + area code (2-9 + 2 digits) + 7 digits

    const mustCompleteProfileSetup = useMemo(() => {
        if (location.pathname !== "/add-admin-profile") {
            return false;
        }
        if (firstTimeSetup || !authHasProfile || isNewProfile) {
            return true;
        }
        // Wait for profile load before allowing navigation away (handles stale hasProfile=true).
        if (loading || loadingRoles || serverProfileSetupComplete === null) {
            return true;
        }
        // Gate only users whose saved profile is still incomplete — not unsaved form edits.
        if (!serverProfileSetupComplete) {
            return true;
        }
        return false;
    }, [
        location.pathname,
        authHasProfile,
        isNewProfile,
        firstTimeSetup,
        loading,
        loadingRoles,
        serverProfileSetupComplete,
    ]);

    const showOwnProfileSections =
        isEditingSelf ||
        (location.pathname === "/add-admin-profile" && mustCompleteProfileSetup);

    // Coaches/staff cannot change roles; team-card edit forces loggedInUserEdit=false so
    // showOwnProfileSections is false — still show Account Access as read-only, not an empty Select….
    const showRoleAsReadOnly = showOwnProfileSections || !isAdminEditor;

    const showPasswordSection = showOwnProfileSections;

    const collectValidationErrors = useCallback((): Record<string, string> => {
        const errors: Record<string, string> = {};

        const firstName = (data.firstName ?? "").trim();
        const lastName = (data.lastName ?? "").trim();
        const title = (data.title ?? "").trim();
        const role = (data.role ?? "").trim();
        const telephone = (data.telephone ?? "").trim();
        const email = (data.email ?? "").trim();

        if (!firstName) {
            errors.firstName = "First name is required.";
        }
        if (!lastName) {
            errors.lastName = "Last name is required.";
        }
        if (!title) {
            errors.title = "Title is required.";
        }
        if (!role) {
            errors.role = "Role is required.";
        }
        if (parseProfileRoleId(data.roleId) == null) {
            errors.role = "Role is required.";
        }
        if (!telephone) {
            errors.telephone = "Phone number is required.";
        }

        const requiresCompanyName =
            (isEditingSelf ||
                (location.pathname === "/add-admin-profile" && mustCompleteProfileSetup)) &&
            (isAdminEditor || isCurrentUserStaff);
        const hasCompanyOrSchool = hasValidCompanyOrSchool({
            companyName: data.companyName,
            cleverSchoolName: data.cleverSchoolName,
            email: data.email,
        });
        if (requiresCompanyName && !hasCompanyOrSchool) {
            errors.companyName =
                authProvider === "clever"
                    ? "School name is required for Clever accounts."
                    : "Company name is required.";
        }

        if (!email) {
            errors.email = "Email is required.";
        } else if (!emailOk) {
            errors.email = "Please enter a valid email address (e.g., user@example.com).";
        }

        if (telephone && !phoneOk) {
            errors.telephone =
                "Please enter a valid US phone number (e.g., (555) 123-4567 or 555-123-4567).";
        }

        return errors;
    }, [
        data,
        authProvider,
        emailOk,
        phoneOk,
        location.pathname,
        mustCompleteProfileSetup,
        isEditingSelf,
        isAdminEditor,
        isCurrentUserStaff,
    ]);

    const profileCardRef = useRef<HTMLElement | null>(null);

    const showValidationErrors = useCallback((): boolean => {
        const errors = collectValidationErrors();
        if (Object.keys(errors).length === 0) {
            return true;
        }
        setFieldErrors(errors);
        setError("Please fix the errors below before saving.");
        setOk("");
        profileCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        return false;
    }, [collectValidationErrors]);

    useEffect(() => {
        setSetupIncomplete?.(mustCompleteProfileSetup);
        return () => setSetupIncomplete?.(false);
    }, [mustCompleteProfileSetup, setSetupIncomplete]);

    useEffect(() => {
        registerValidationHandler?.(showValidationErrors);
        return () => registerValidationHandler?.(null);
    }, [showValidationErrors, registerValidationHandler]);

    const handleCancelClick = () => {
        if (mustCompleteProfileSetup) {
            showValidationErrors();
            return;
        }
        setFieldErrors({});
        setError("");
        onClose?.();
    };

    const handleDeleteStaffMember = () => {
        setShowDeleteModal(true);
    };

    const confirmDeleteStaffMember = async () => {
        if (!userId) return;

        const isAdministrator = canon(data.role) === "administrator";
        const deletePath = isAdministrator
            ? `api/company/administrator/${userId}`
            : `api/company/member/${userId}`;

        setDeletingStaff(true);
        try {
            const response = await fetch(buildApiUrl(deletePath), {
                method: 'DELETE',
                credentials: 'include',
            });

            if (response.status === 204) {
                // Success - close modal and navigate back to team page
                setShowDeleteModal(false);
                onClose?.();
                navigate('/team');
            } else if (response.status === 403) {
                const error = await response.json().catch(() => ({ detail: 'Permission denied' }));
                setError(error.detail || 'You do not have permission to delete this staff member');
                setShowDeleteModal(false);
            } else if (response.status === 400) {
                const error = await response.json().catch(() => ({ detail: 'Invalid request' }));
                setError(error.detail || 'Cannot delete your own account');
                setShowDeleteModal(false);
            } else if (response.status === 404) {
                const body = await response.json().catch(() => ({} as { detail?: string }));
                const detail =
                    typeof body.detail === "string" && body.detail.trim()
                        ? body.detail
                        : "Team member not found";
                setError(detail);
                setShowDeleteModal(false);
            } else if (response.status >= 500) {
                setError('There was an issue deleting this team member. Please contact support@polarisathlete.com for assistance.');
                setShowDeleteModal(false);
            } else {
                const error = await response.json().catch(() => ({ detail: 'Failed to delete staff member' }));
                setError(error.detail || 'Failed to delete staff member');
                setShowDeleteModal(false);
            }
        } catch (error) {
            setError('Network error. Please try again.');
            setShowDeleteModal(false);
        } finally {
            setDeletingStaff(false);
        }
    };

    async function onSave() {
        setError("");
        setOk("");
        setFieldErrors({});

        const errors = collectValidationErrors();

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setError("Please fix the errors below before saving.");
            return;
        }

        if (roleOptions && roleOptions.length > 0) {
            const m =
                roleOptions.find((o) => o.id === data.roleId) ||
                roleOptions.find((o) => canon(o.name) === canon(data.role));
            if (m) { data.role = m.name; data.roleId = m.id; }
        }

        setSaving(true);
        try {
            // Create a copy of data with relative URLs for backend storage
            const dataToSave: any = {
                ...data,
                profileImageUrl: toRelative(data.profileImageUrl),
                sportIds: data.sportIds ?? [],
                sportId: (data.sportIds ?? [])[0] ?? data.sportId ?? null,
                internalCoachPreReadEnabled: !!data.internalCoachPreReadEnabled,
                manageStudentAthletesEnabled: !!data.manageStudentAthletesEnabled,
            };
            const roleId = parseProfileRoleId(data.roleId);
            if (roleId != null) {
                dataToSave.roleId = String(roleId);
            } else {
                delete dataToSave.roleId;
            }

            // Never send companyLogoUrl when we have a pending logo file: the logo is not on the
            // server yet (upload runs after save). Sending empty/blob would overwrite or corrupt
            // the stored logo. When we have no pending logo, exclude it so backend preserves existing.
            // The upload step (after save) is the only place that sets the company logo on the server.
            delete dataToSave.companyLogoUrl;

            // Save the profile data first
            const res = await fetch(saveUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                credentials: "include", // Send HTTP-only cookies
                body: JSON.stringify(dataToSave ?? {}),
            });
            if (!res.ok) {
                setError(
                    await parseApiErrorMessage(
                        res,
                        "We couldn't save your profile. Please check the form and try again.",
                    ),
                );
                return;
            }

            const savedSetupComplete = isAdminStaffProfileSetupComplete({
                title: data.title,
                telephone: data.telephone,
                companyName: data.companyName,
                cleverSchoolName: data.cleverSchoolName,
                email: data.email,
            });

            setIsNewProfile(false);
            setServerProfileSetupComplete(savedSetupComplete);
            if (savedSetupComplete) {
                setHasProfile(true);
                setSetupIncomplete?.(false);
            }

            // Set timestamp in localStorage to notify other components
            localStorage.setItem('profileLastUpdated', Date.now().toString());

            // Now upload avatar if we have one
            if (pendingAvatar) {
                try {
                    // Use the userId prop if provided (admin editing team member)
                    const userIdNumber = userId ? (typeof userId === 'string' ? parseInt(userId, 10) : userId) : undefined;

                    // Pass userId only if it's valid and user is editing someone else
                    const uploadResult = await uploadUserAvatar(pendingAvatar, userIdNumber);

                    if (!uploadResult.success) {
                        // Don't fail the save, just show warning
                        setError(`Profile saved, but avatar upload failed.`);
                    } else {
                        // Set timestamp for cache-busting
                        setAvatarUploadTimestamp(Date.now());

                        // Clear the avatar cache so fresh image is fetched on next page load
                        if (userIdNumber) {
                            // Clear cache for specific user (admin editing team member)
                            clearUserAvatarCache(userIdNumber);
                        } else {
                            // Clear cache for current user (editing own profile)
                            clearMyAvatarCache();
                        }
                    }
                } catch (uploadError) {
                    setError(`Profile saved, but avatar upload failed: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
                }
            }

            // Upload company logo if we have one (admin only)
            if (pendingLogo && isAdminEditor) {
                // Determine which username to use for the upload
                // Always use data.email as the username for consistency
                const uploadUsername = data.email;

                if (!uploadUsername) {
                    setError(`Profile saved, but logo upload failed: Unable to determine user`);
                } else {
                    try {
                        const uploadResult = await uploadCompanyLogo(pendingLogo);
                        if (!uploadResult.success) {
                            setError(`Profile saved, but logo upload failed: ${uploadResult.error}`);
                        } else {
                            // Clear the cache for this user to force a fresh fetch
                            clearCompanyLogoCache(uploadUsername);
                            // Re-fetch the logo blob URL so the UI displays the newly uploaded logo
                            try {
                                const newLogoBlobUrl = await fetchCompanyLogoBlobUrl(uploadUsername, true);
                                setCompanyLogoBlobUrl(newLogoBlobUrl);
                            } catch {
                                // Non-fatal — logo will reload on next page visit
                            }
                        }
                    } catch (logoError) {
                        setError(`Profile saved, but logo upload failed: ${logoError instanceof Error ? logoError.message : 'Unknown error'}`);
                    }
                }
            }

            // Reload profile data to get the updated avatar/logo URLs from backend BEFORE clearing pending files
            let savedCompanyLogoUrl: string | undefined = toAbsoluteUrl(data.companyLogoUrl);
            try {
                const reloadRes = await fetch(loadUrl, {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    cache: "no-store",
                });

                if (reloadRes.ok) {
                    const reloadedProfile = await reloadRes.json();
                    const savedCompanyName = (data.companyName ?? "").trim();
                    const reloadEmail = (reloadedProfile.email ?? data.email ?? "").trim();
                    let reloadedCompanyName = (reloadedProfile.companyName ?? "").trim();
                    if (isPlaceholderCompanyName(reloadedCompanyName, reloadEmail)) {
                        reloadedCompanyName = "";
                    }
                    const cleverSchoolFromServer = reloadedProfile.cleverSchoolName ?? (authProvider === "clever" && savedCompanyName ? savedCompanyName : undefined);
                    const reloadedLogoUrl = toAbsoluteUrl(
                        reloadedProfile.companyLogoUrl || reloadedProfile.company_logo_url
                    );
                    if (reloadedLogoUrl) {
                        savedCompanyLogoUrl = reloadedLogoUrl;
                    }
                    // Merge avatar/logo URLs and server fields. Update serverCleverSchoolName so lock is based on load/save only.
                    setServerCleverSchoolName(typeof cleverSchoolFromServer === "string" ? cleverSchoolFromServer : undefined);
                    setData((prevData) => ({
                        ...prevData,
                        profileImageUrl: toAbsoluteUrl(reloadedProfile.profileImageUrl) ?? prevData.profileImageUrl,
                        companyLogoUrl: reloadedLogoUrl ?? prevData.companyLogoUrl,
                        ...(reloadedProfile.companyName !== undefined && { companyName: reloadedCompanyName || prevData.companyName }),
                        ...(cleverSchoolFromServer !== undefined && { cleverSchoolName: cleverSchoolFromServer }),
                    }));
                    if (reloadEmail) {
                        clearCompanyLogoCache(reloadEmail);
                        await refreshCompanyLogoPreview(reloadEmail, true);
                    }
                }
            } catch {
                // Non-fatal error, continue with save success flow
            }

            // Clear pending files after reload to ensure fresh URL is available
            setPendingAvatar(null);
            setPendingLogo(null);

            // Callback to parent component
            onSaved?.(data);

            // Delay to allow backend to process uploaded images before notifying components
            // This ensures the brand banner can fetch the new profile picture
            await new Promise(resolve => setTimeout(resolve, 500));

            // Include reloaded presigned companyLogoUrl so the rail sidebar can update
            // without calling /api/company/me/logo.
            window.dispatchEvent(new CustomEvent('profileUpdated', {
                detail: { ...data, companyLogoUrl: savedCompanyLogoUrl },
            }));
            window.dispatchEvent(new CustomEvent('profilePictureUpdated'));

            // Only leave this page when first-time setup is actually complete (avoids blank form while navigating)
            if (savedSetupComplete) {
                if (afterSaveTo) {
                    const target = typeof afterSaveTo === "function" ? afterSaveTo(data) : afterSaveTo;
                    if (target) {
                        setNavigating(true);
                        navigate(target);
                        return;
                    }
                }
                onClose?.();
            } else if (location.pathname === "/add-admin-profile") {
                setOk("Please complete all required fields (including company name) to continue.");
            }
        } catch (e: any) {
            setError(e?.message || "Failed to save.");
        } finally {
            setSaving(false);
        }
    }

    const normalizedOptions: RSOption[] =
        roleOptions && roleOptions.length > 0 ? roleOptions
            : (roles ?? []).map<RSOption>((name, i) => ({ id: i + 1, name }));

    const roleSelectDisabled = loading || loadingRoles || !isAdminEditor || isCurrentUserStaff || isNewProfile;

    // Add cache-busting timestamp to avatar URL if image was recently uploaded
    const avatarUrl = useMemo(() => {
        if (avatarPreview) return avatarPreview;

        const baseUrl = toAbsoluteUrl(data.profileImageUrl);
        if (!baseUrl) return "";

        // Add cache-busting parameter if avatar was recently uploaded
        if (avatarUploadTimestamp > 0) {
            const separator = baseUrl.includes('?') ? '&' : '?';
            return `${baseUrl}${separator}_t=${avatarUploadTimestamp}`;
        }

        return baseUrl;
    }, [avatarPreview, data.profileImageUrl, avatarUploadTimestamp]);

    // Use companyLogoBlobUrl (fetched blob) if available, otherwise fall back to the URL from data
    const logoUrl =
        logoPreview ??
        resolveCompanyLogoDisplayUrl(data.companyLogoUrl, companyLogoBlobUrl) ??
        "";

    // Memoize the userEmail prop to prevent ProfileAvatar from re-rendering on every keystroke
    const avatarUserEmail = useMemo(() => {
        const result = isEditingSelf ? undefined : (initialEmailRef.current || undefined);
        return result;
    }, [isEditingSelf]);


    // Wait for both profile data and role options to load before rendering form
    // This prevents the "Select..." dropdown from showing before data is ready
    // Don't show loading screen if we're navigating away
    if ((loading || loadingRoles) && !navigating) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
                fontSize: '16px',
                color: '#666'
            }}>
                Loading profile...
            </div>
        );
    }

    // If navigating, return null to hide the form completely
    if (navigating) {
        return null;
    }

    return (
        <>
            <section ref={profileCardRef} className="card form-card profile-card" aria-busy={saving}>
                <div className="form-head">
                    <h2 className="form-title">Profile</h2>
                </div>

                {(error || ok) && <div className={`form-alert ${error ? "is-error" : "is-ok"}`}>{error || ok}</div>}

                {/* Avatar */}
                <div className="pf-top">
                    <div className="pf-avatar">
                        <ProfileAvatar
                            size="large"
                            className="pf-avatar-img"
                            allowUpload={false}
                            alt="Profile picture"
                            previewUrl={avatarPreview || undefined}
                            imageUrl={avatarUrl || undefined}
                            displayName={`${data.firstName} ${data.lastName}`.trim()}
                            skipAvatarFetch={true}
                        />
                    </div>
                    {!isTeamMemberEdit && (
                        <button className="btn-chip" type="button" onClick={() => setShowUploadAvatar(true)}>
                            <span className="plus">+</span> Add
                        </button>
                    )}
                </div>

                {showUploadAvatar && (
                    <UploadImageModal
                        open={showUploadAvatar}
                        onClose={() => {
                            setShowUploadAvatar(false);
                            // Don't clear pendingAvatar here - user might have selected a file
                        }}
                        mode="deferred"                          // IMPORTANT: don't upload yet
                        onSelected={(file) => {
                            setPendingAvatar(file);
                            setShowUploadAvatar(false);
                        }}
                        useCookies={useCookies}
                        title="Select profile image"
                    />
                )}

                {/* Fields */}
                <div className="grid-2">
                    <div className="field">
                        <label>First name*</label>
                        <input
                            className={`input${fieldErrors.firstName ? " input-error" : ""}`}
                            value={data.firstName}
                            onChange={(e) => {
                                set("firstName")(e);
                                if (fieldErrors.firstName) {
                                    setFieldErrors(prev => {
                                        const { firstName, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                        />
                        {fieldErrors.firstName && <div className="field-error">{fieldErrors.firstName}</div>}
                    </div>

                    <div className="field">
                        <label>Last name*</label>
                        <input
                            className={`input${fieldErrors.lastName ? " input-error" : ""}`}
                            value={data.lastName}
                            onChange={(e) => {
                                set("lastName")(e);
                                if (fieldErrors.lastName) {
                                    setFieldErrors(prev => {
                                        const { lastName, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                        />
                        {fieldErrors.lastName && <div className="field-error">{fieldErrors.lastName}</div>}
                    </div>

                    <div className="field">
                        <label>Email*</label>
                        <input className="input" type="email" value={data.email} onChange={set("email")} readOnly={!isNewProfile && !!data.email} />
                        {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
                    </div>

                    <div className="field">
                        <label>Title*</label>
                        <input
                            className={`input${fieldErrors.title ? " input-error" : ""}`}
                            value={data.title}
                            onChange={(e) => {
                                set("title")(e);
                                if (fieldErrors.title) {
                                    setFieldErrors(prev => {
                                        const { title, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                        />
                        {fieldErrors.title && <div className="field-error">{fieldErrors.title}</div>}
                    </div>

                    <div className="field">
                        <label>Account access*</label>
                        {showRoleAsReadOnly ? (
                            // Show read-only label when the viewer cannot change roles
                            <div className="field-readonly">
                                <span className="readonly-value">{formatRoleLabel(data.role)}</span>
                                <span className="readonly-note">
                                    {showOwnProfileSections
                                        ? "Your account access is preset based on your role"
                                        : "Account access is set by a staff member or administrator"}
                                </span>
                            </div>
                        ) : (
                            // Show editable dropdown for administrators editing other users' profiles
                            <div className="select-wrap">
                                <RoleSelect
                                    options={normalizedOptions}
                                    valueId={data.roleId ?? null}
                                    valueName={data.role ?? null}
                                    disabled={roleSelectDisabled}
                                    onChange={(opt: RSOption) => {
                                        setData((s) => ({
                                            ...s,
                                            role: opt.name,
                                            roleId: opt.id,
                                            internalCoachPreReadEnabled: false,
                                            manageStudentAthletesEnabled: false,
                                            ...(canon(opt.name) === "coach" ? {} : { sportIds: [], sportId: undefined }),
                                        }));
                                        // Clear role error when user selects a role
                                        if (fieldErrors.role) {
                                            setFieldErrors(prev => ({ ...prev, role: "" }));
                                        }
                                    }}
                                />
                            </div>
                        )}
                        {fieldErrors.role && <div className="field-error">{fieldErrors.role}</div>}
                    </div>

                    <div className="field">
                        <label>Phone number*</label>
                        <input
                            className={`input${fieldErrors.telephone ? " input-error" : ""}`}
                            value={data.telephone}
                            onChange={(e) => {
                                set("telephone")(e);
                                if (fieldErrors.telephone) {
                                    setFieldErrors(prev => {
                                        const { telephone, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                        />
                        {fieldErrors.telephone && <div className="field-error">{fieldErrors.telephone}</div>}
                    </div>

                    {canon(data.role) === "coach" && (
                        <CoachSportsField
                            sportIds={data.sportIds ?? []}
                            onChange={(sportIds) => setData((s) => ({ ...s, sportIds, sportId: sportIds[0] }))}
                        />
                    )}

                    {isTeamMemberEdit && (
                        <TeamMemberConfigurations
                            role={data.role}
                            internalCoachPreReadEnabled={!!data.internalCoachPreReadEnabled}
                            manageStudentAthletesEnabled={!!data.manageStudentAthletesEnabled}
                            onInternalCoachPreReadChange={(enabled) =>
                                setData((s) => ({ ...s, internalCoachPreReadEnabled: enabled }))
                            }
                            onManageStudentAthletesChange={(enabled) =>
                                setData((s) => ({ ...s, manageStudentAthletesEnabled: enabled }))
                            }
                        />
                    )}

                    {/* Company name + logo when editing own profile (admin/staff), incl. first-time /add-admin-profile */}
                    {showOwnProfileSections && (isAdminEditor || isCurrentUserStaff) && (
                        <>
                            <div className="field">
                                <label>
                                    {authProvider === "clever" ? "School name*" : "Company name*"}
                                    {data.cleverSchoolName && (
                                        <span className="clever-badge" title="Pre-filled from Clever">
                                            Clever
                                        </span>
                                    )}
                                </label>
                                {(() => {
                                    const displayValue = data.cleverSchoolName || data.companyName || "";
                                    const hasSavedCleverSchool =
                                        (serverCleverSchoolName ?? "").trim().length > 0;
                                    const isStaffUser = isCurrentUserStaff;
                                    const isClever = authProvider === "clever";
                                    const isAdminFromProfile = canon(data.role) === "administrator";

                                    // Clever + no server school name + (profile or auth says admin): always editable – override staff/lock so they can type and save.
                                    const forceEditable =
                                        isClever && !hasSavedCleverSchool && (isAdminFromProfile || isAdminEditor);
                                    const shouldLock = forceEditable
                                        ? false
                                        : isStaffUser || (isClever && isAdminEditor && hasSavedCleverSchool);

                                    return (
                                        <input
                                            className={`input${fieldErrors.companyName ? " input-error" : ""}`}
                                            value={displayValue}
                                            onChange={
                                                hasSavedCleverSchool
                                                    ? undefined
                                                    : (e) => {
                                                        set("companyName")(e);
                                                        if (fieldErrors.companyName) {
                                                            setFieldErrors(prev => {
                                                                const { companyName, ...rest } = prev;
                                                                return rest;
                                                            });
                                                        }
                                                    }
                                            }
                                            readOnly={shouldLock}
                                            disabled={shouldLock}
                                        />
                                    );
                                })()}
                                {data.cleverSchoolName && (
                                    <div className="clever-field-note">
                                        School name is set from your Clever account and cannot be changed.
                                    </div>
                                )}
                                {fieldErrors.companyName && <div className="field-error">{fieldErrors.companyName}</div>}
                            </div>

                            {/* Clever district name + SIS ID (no raw Clever district/school object IDs) */}
                            {authProvider === "clever" && (data.cleverDistrictName || data.cleverSisId) && (
                                <div className="clever-id-fields">
                                    {data.cleverDistrictName && (
                                        <div className="field">
                                            <label>District name</label>
                                            <input
                                                className="input input--readonly"
                                                value={data.cleverDistrictName}
                                                readOnly
                                                disabled
                                            />
                                        </div>
                                    )}
                                    {data.cleverSisId && (
                                        <div className="field">
                                            <label>Clever SIS ID</label>
                                            <input
                                                className="input input--readonly"
                                                value={data.cleverSisId}
                                                readOnly
                                                disabled
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="field">
                                <label>Company or Program Logo</label>
                                <div className="pf-logo-row">
                                    <div className="pf-logo-img">
                                        {logoUrl && !logoImageError ? (
                                            <img
                                                src={logoUrl}
                                                alt="Company logo"
                                                onError={() => {
                                                    setLogoImageError(true);
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#f3f4f6'
                                                }}
                                            >
                                                {/* Placeholder crosshatch pattern in circular container */}
                                                <svg width="100%" height="100%" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <defs>
                                                        <clipPath id="circleClip">
                                                            <circle cx="40" cy="40" r="40"/>
                                                        </clipPath>
                                                    </defs>
                                                    <g clipPath="url(#circleClip)">
                                                        <rect x="0" y="0" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="16" y="0" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="32" y="0" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="48" y="0" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="64" y="0" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="0" y="16" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="16" y="16" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="32" y="16" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="48" y="16" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="64" y="16" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="0" y="32" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="16" y="32" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="32" y="32" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="48" y="32" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="64" y="32" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="0" y="48" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="16" y="48" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="32" y="48" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="48" y="48" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="64" y="48" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="0" y="64" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="16" y="64" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="32" y="64" width="16" height="16" fill="#e5e7eb"/>
                                                        <rect x="48" y="64" width="16" height="16" fill="#d1d5db"/>
                                                        <rect x="64" y="64" width="16" height="16" fill="#e5e7eb"/>
                                                    </g>
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    {isAdminEditor && (
                                        <button className="btn-chip" type="button" onClick={handleLogoUploadClick}>
                                            <span className="pen">✎</span> Upload
                                        </button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {showUploadLogo && (
                    <UploadImageModal
                        open={showUploadLogo}
                        onClose={() => setShowUploadLogo(false)}
                        mode="deferred"                          // IMPORTANT: don't upload yet
                        onSelected={(file) => setPendingLogo(file)}
                        useCookies={useCookies}
                        title="Select company logo"
                    />
                )}

                {/* Bottom actions row - Delete Account Link and Cancel/Save buttons */}
                <div className="delete-account-row">
                    {isTeamMemberEdit && isAdminEditor ? (
                        <button
                            type="button"
                            className="link-danger"
                            onClick={handleDeleteStaffMember}
                            disabled={deletingStaff || saving}
                        >
                            Delete team member
                        </button>
                    ) : (
                        <div></div>
                    )}
                    <div className="form-head-actions">
                        <button type="button" className="btn-cancel" onClick={handleCancelClick}>
                            Cancel
                        </button>
                        <button
                            className="btn-pill"
                            onClick={onSave}
                            disabled={saving}
                            aria-busy={saving}
                        >
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </div>
            </section>

            {/* Delete Team Member Confirmation Modal */}
            {isTeamMemberEdit && isAdminEditor && (
                <DeleteTeamMemberModal
                    open={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={confirmDeleteStaffMember}
                    staffMemberName={`${data.firstName} ${data.lastName}`.trim()}
                    isDeleting={deletingStaff}
                />
            )}

            {/* Password form: own-profile edits, or first-time /add-admin-profile setup (incl. after re-login) */}
            {showPasswordSection && (
                <PasswordForm
                    saveUrl={buildApiUrl("api/auth/password")}
                    useCookies={useCookies}
                    skipCurrentPassword={requirePasswordChange}
                    onChanged={async () => {
                        await onPasswordChanged?.();
                    }}
                    onCancel={requirePasswordChange ? undefined : () => {
                        if (mustCompleteProfileSetup) {
                            showValidationErrors();
                            return;
                        }
                        setFieldErrors({});
                        setError("");
                        if (afterSaveTo) {
                            const target = typeof afterSaveTo === "function" ? afterSaveTo(data) : afterSaveTo;
                            if (target) {
                                navigate(target);
                                return;
                            }
                        }
                        onClose?.();
                    }}
                    showDeleteAccount={showDeleteAccount}
                    onDeleteAccount={onDeleteAccount}
                />
            )}
        </>
    );
}

